import { getGradedGraphemeClosures } from "@-/dev.improve.haniverse.data.component-prevalence";
import { graphemeFrequencyRanks } from "@-/dev.improve.haniverse.data.graphemes-frequency";
import { getTaiwanUsageMap } from "@-/dev.improve.haniverse.data.taiwan-usage-map";
import {
  type CorpusSettings,
  resolveCorpusSettings,
  type SimplifiedCharacterTier,
  type TraditionalCharacterUsage,
} from "@-/dev.improve.haniverse.lib.morpheme.corpus.settings";
import type { HanziScript } from "@-/dev.improve.haniverse.lib.actor.types";
import { getTierByGraphemeDict } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.data.prc.tier";

/** The per-corpus word ranks a word row / morpheme slice already carries. */
export type WordCorpusRanks = {
  /** BIAU word rank (traditional corpus). */
  biau2Rank: number | null;
  /** SUBTLEX-CH-WF rank (simplified corpus). */
  subtlexWfRank: number | null;
};

/**
 * The user's browsing corpus for the active script, resolved once so every non-curated surface asks
 * the same question the same way. Character and word membership are independent: a word is never
 * hidden because one of its characters is.
 */
export type MorphemeCorpusFilter = {
  /**
   * Serializable identity of the filter (script + that script's settings). Put THIS in query keys:
   * the predicates are functions, which a query-key hash silently drops.
   */
  cacheKey: string;
  hanziScript: HanziScript;
  /** True when every character passes (all bands on, no rank cutoff): callers may skip filtering. */
  includesAllGraphemes: boolean;
  /** True when every word passes (no rank cutoff): callers may skip filtering. */
  includesAllWords: boolean;
  isGraphemeInCorpus: (glyph: string) => boolean;
  isWordInCorpus: (ranks: WordCorpusRanks) => boolean;
  /** Worst word rank (inclusive) in the active script's corpus, or `null` for no cutoff. */
  wordMaxRank: number | null;
};

function firstScalarCharacter(glyph: string): string {
  const codePoint = glyph.codePointAt(0);
  return codePoint === undefined ? glyph : String.fromCodePoint(codePoint);
}

function simplifiedCharacterTier(character: string): SimplifiedCharacterTier {
  const tier = getTierByGraphemeDict()[character];
  return tier === undefined ? "unlisted" : `tier${tier}`;
}

function traditionalCharacterUsage(character: string): TraditionalCharacterUsage {
  return getTaiwanUsageMap()[character] ?? "unlisted";
}

function isWithinMaxRank(rank: number | null | undefined, maxRank: number | null): boolean {
  return maxRank === null || (rank != null && rank <= maxRank);
}

/** Whether `glyph` is a graded character or one of its components (see `getGradedGraphemeClosures`). */
function isInGradedClosure(
  closure: ReadonlySet<string>,
  glyph: string,
  character: string,
): boolean {
  return closure.has(glyph) || closure.has(character);
}

/**
 * Build the corpus filter for `hanziScript` from the user's (already resolved) corpus settings.
 * Simplified: PRC 通用规范汉字表 tier + SUBTLEX-CH ranks. Traditional: Taiwan MOE chart + BIAU ranks.
 * A rank cutoff also hides every character/word the corpus has no rank for.
 *
 * The most common band (`tier1` / `common`) also covers every graded character — any HSK syllabus
 * for simplified, TBCL for traditional — and every primitive inside one, so the building blocks a
 * learner meets are never hidden as rare just because the charts do not list them.
 */
export function createMorphemeCorpusFilter(
  hanziScript: HanziScript,
  corpusSettings: CorpusSettings,
): MorphemeCorpusFilter {
  const gradedGraphemeClosures = getGradedGraphemeClosures();
  if (hanziScript === "simplified") {
    const { characterMaxRank, characterTiers, wordMaxRank } = corpusSettings.simplified;
    const tiers = new Set(characterTiers);
    const includesGraded = tiers.has("tier1");
    return {
      cacheKey: JSON.stringify([hanziScript, [...tiers].sort(), characterMaxRank, wordMaxRank]),
      hanziScript,
      includesAllGraphemes: tiers.size === 4 && characterMaxRank === null,
      includesAllWords: wordMaxRank === null,
      isGraphemeInCorpus: (glyph) => {
        const character = firstScalarCharacter(glyph);
        return (
          (tiers.has(simplifiedCharacterTier(character)) ||
            (includesGraded &&
              isInGradedClosure(gradedGraphemeClosures.simplified, glyph, character))) &&
          isWithinMaxRank(graphemeFrequencyRanks.subtlex[character], characterMaxRank)
        );
      },
      isWordInCorpus: ({ subtlexWfRank }) => isWithinMaxRank(subtlexWfRank, wordMaxRank),
      wordMaxRank,
    };
  }
  const { characterMaxRank, characterUsages, wordMaxRank } = corpusSettings.traditional;
  const usages = new Set(characterUsages);
  const includesGraded = usages.has("common");
  return {
    cacheKey: JSON.stringify([hanziScript, [...usages].sort(), characterMaxRank, wordMaxRank]),
    hanziScript,
    includesAllGraphemes: usages.size === 4 && characterMaxRank === null,
    includesAllWords: wordMaxRank === null,
    isGraphemeInCorpus: (glyph) => {
      const character = firstScalarCharacter(glyph);
      return (
        (usages.has(traditionalCharacterUsage(character)) ||
          (includesGraded &&
            isInGradedClosure(gradedGraphemeClosures.traditional, glyph, character))) &&
        isWithinMaxRank(graphemeFrequencyRanks.biau[character], characterMaxRank)
      );
    },
    isWordInCorpus: ({ biau2Rank }) => isWithinMaxRank(biau2Rank, wordMaxRank),
    wordMaxRank,
  };
}

/**
 * Rebuild a filter from its {@link MorphemeCorpusFilter.cacheKey} — how the corpus crosses a
 * process boundary (e.g. the Worker's `/search`). A malformed key yields the script defaults
 * rather than an unfiltered corpus.
 */
export function morphemeCorpusFilterFromCacheKey(cacheKey: string): MorphemeCorpusFilter {
  let parsed: unknown;
  try {
    parsed = JSON.parse(cacheKey);
  } catch {
    parsed = null;
  }
  const [hanziScript, bands, characterMaxRank, wordMaxRank] = Array.isArray(parsed) ? parsed : [];
  const settings = resolveCorpusSettings({
    simplified: { characterMaxRank, characterTiers: bands, wordMaxRank },
    traditional: { characterMaxRank, characterUsages: bands, wordMaxRank },
  });
  return createMorphemeCorpusFilter(
    hanziScript === "traditional" ? "traditional" : "simplified",
    settings,
  );
}
